import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Card } from "../components/UI/card";
import { Button } from "../components/UI/button";
import { Phone, Mail, ArrowRight } from "lucide-react";

// IMPORT YOUR LOGO
import logo from "../assets/FLUXLAB-Logo.png";

// -------------------------------------
// IMPORT YOUR PRODUCT IMAGES HERE
import plastic1 from "../assets/products/Plasticpart.png"
import plastic2 from "../assets/products/plasticpart-2.png"
import plastic3 from "../assets/products/PlasticPart-3.png"
import plastic4 from "../assets/products/PlasticPart-4.png"
import plastic5 from "../assets/products/PlasticPart-5.png"
import plastic6 from "../assets/products/PlasticPart-6.png"
import plastic7 from "../assets/products/PlasticPart-7.png"
import plastic8 from "../assets/products/PlasticPart-8.png"


import handlerelease from "../assets/products/HandleRelease.png"
import hookovermold from "../assets/products/HookOvermold.png"
import housinglh from "../assets/products/Housing-LH.png"
import housing from "../assets/products/Housing.png"
import flexmate from "../assets/products/Flexmate.png"
import lockovermold from "../assets/products/LockOvermold.png"
import mainhub from "../assets/products/Mainhub.png"
import motorbridge from "../assets/products/motorbridge.png"
// -------------------------------------

const Products = () => {
  const [scrollY, setScrollY] = useState(0);
  const { scrollYProgress } = useScroll();
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.7]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // -------------------------------------
  // PRODUCT LIST WITH IMAGE SUPPORT
  // -------------------------------------

  const otherProducts = [
    { name: "Handle Release", image: handlerelease },
    { name: "Hook Overmould", image: hookovermold },
    { name: "Housing", image: housing },
    { name: "Housing-LH", image: housinglh },
    { name: "Lock Overmould", image: lockovermold },
    { name: "FlexMate", image: flexmate },
    { name: "Main Hub", image: mainhub },
    { name: "Motor Bridge", image: motorbridge },
  ];

  const plasticParts = [
    { name: "Release CAM", image: plastic1 },
    { name: "Strap Bezel", image: plastic2 },
    { name: "Sleeve Bracket", image: plastic3 },
    { name: "Bearing Cover", image: plastic4 },
    { name: "Sleeve", image: plastic5 },
    { name: "Bracket Locator", image: plastic6 },
    { name: "Striker", image: plastic7 },
    { name: "Switch Cover", image: plastic8 },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* -------------------------------------------------------
           HEADER (Copied from Home.jsx)
      -------------------------------------------------------- */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
        style={{ opacity: headerOpacity }}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={scrollToTop}>
            <img src={logo} alt="Company Logo" className="w-[110px] h-auto" />
          </div>

          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors">Home</a>
            <a href="/products" className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors">Products</a>
            <a href="/#services" className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors">Services</a>
            <a href="/#contact" className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors">Contact</a>
          </div>

          <a href="/#contact">
            <Button className="bg-[#2563eb] hover:bg-red-700 text-white flex items-center">
              <Phone className="w-4 h-4 mr-2" />
              Get Quote
            </Button>
          </a>
        </div>
      </motion.nav>

      {/* -------------------------------------------------------
           PAGE CONTENT
      -------------------------------------------------------- */}

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-24">

        {/* Page Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-blue-600 font-semibold mb-3">Our Products</div>
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Precision Plastic Components</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Explore our premium range of automotive-grade molded parts.
          </p>
        </motion.div>

        {/* ---------------------
             PLASTIC PARTS
        ---------------------- */}
        <h2 className="text-3xl font-bold text-slate-900 mb-6">Plastic Parts</h2>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 mb-20">
          {plasticParts.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-200 rounded-2xl bg-white h-full">

                {/* Image Box */}
                <div className="w-full h-48 bg-slate-100 overflow-hidden">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      Image Coming Soon
                    </div>
                  )}
                </div>

                {/* Text Content */}
                <div className="p-5 text-left">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    High-quality molded part with superior durability and precision.
                  </p>
{/* 
                  <div className="flex items-center text-blue-600 cursor-pointer group-hover:gap-2 transition-all">
                    Know More <ArrowRight className="ml-1 w-4 h-4" />
                  </div> */}
                </div>

              </Card>
            </motion.div>
          ))}
        </div>

        {/* ---------------------
             OTHER PRODUCTS
        ---------------------- */}
        <h2 className="text-3xl font-bold text-slate-900 mb-6">Other Products</h2>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10">
          {otherProducts.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-200 rounded-2xl bg-white h-full">

                {/* Image Box */}
                <div className="w-full h-48 bg-slate-100 overflow-hidden">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      Image Coming Soon
                    </div>
                  )}
                </div>

                {/* Text Content */}
                <div className="p-5 text-left">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Custom engineered part built with premium-grade material.
                  </p>

                  {/* <div className="flex items-center text-blue-600 cursor-pointer group-hover:gap-2 transition-all">
                    Know More <ArrowRight className="ml-1 w-4 h-4" />
                  </div> */}
                </div>

              </Card>
            </motion.div>
          ))}
        </div>

      </div>

      {/* -------------------------------------------------------
           FOOTER (Copied from Home.jsx)
      -------------------------------------------------------- */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">

            {/* Company Info */}
            <div className="text-left">
              <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={scrollToTop}>
                <img src={logo} alt="FLUXLAB Logo" className="w-[110px] h-auto" />
              </div>
              <p className="text-sm leading-relaxed">
                Leading manufacturer of automotive plastic components with a decade of excellence.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="/products" className="hover:text-white transition-colors">Products</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="/#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 text-blue-500" />
                  <span>+91 9099159922</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 text-blue-500" />
                  <span>sales@fluxlab.in</span>
                </li>
              </ul>
            </div>

          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>
              &copy; {new Date().getFullYear()} FLUXLAB. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Products;
